import React, { useEffect, useState, useCallback, type ReactNode } from 'react'
import { useApp } from '../lib/context'
import { useT, useI18n } from '../lib/i18n'
import { triggerForceSync } from '../lib/api'
import type { View, ProjectStatus } from '../lib/types'
import type { Alert } from '../lib/api'
import ColorLegend from './ColorLegend'
import { resetPersistedPreferences } from '../lib/usePersistedState'
import { useDesktopNotif } from '../lib/useDesktopNotif'
import './styles/layout.css'

/* ── Icônes SVG inline — pas de dépendance externe ────────────── */
const NAV_ICONS: Record<string, React.ReactNode> = {
  overview: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="1" width="6" height="6" rx="1.5"/>
      <rect x="9" y="1" width="6" height="6" rx="1.5"/>
      <rect x="1" y="9" width="6" height="6" rx="1.5"/>
      <rect x="9" y="9" width="6" height="6" rx="1.5"/>
    </svg>
  ),
  progress: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="9" width="3" height="6" rx="1"/>
      <rect x="6.5" y="5" width="3" height="10" rx="1"/>
      <rect x="12" y="1" width="3" height="14" rx="1"/>
    </svg>
  ),
  tasks: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <rect x="2" y="2" width="12" height="12" rx="2"/>
      <path d="M5 8l2 2 4-4"/>
    </svg>
  ),
  journal: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="8" cy="8" r="6"/>
      <path d="M8 4.5V8l2.5 2"/>
    </svg>
  ),
  kanban: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="2" width="4" height="12" rx="1"/>
      <rect x="6" y="2" width="4" height="8"  rx="1"/>
      <rect x="11" y="2" width="4" height="10" rx="1"/>
    </svg>
  ),
  compare: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1v14M1 8h6M15 8h-6"/>
      <path d="M3 5.5L1 8l2 2.5M13 5.5l2 2.5-2 2.5"/>
    </svg>
  ),
  evolution: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1,12 5,7 9,9 15,3"/>
      <polyline points="11,3 15,3 15,7"/>
    </svg>
  ),
  gitdiff: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="4" cy="4" r="2"/>
      <circle cx="12" cy="12" r="2"/>
      <path d="M4 6v2a4 4 0 004 4h1"/>
      <path d="M10 3h3v3"/>
    </svg>
  ),
  tokens: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1l1.8 3.6L14 5.3l-3 2.9.7 4.1L8 10.4l-3.7 1.9.7-4.1L2 5.3l4.2-.7z"/>
    </svg>
  ),
}

/* ── Labels lisibles des vues pour la topbar ───────────────────── */
const VIEW_LABELS: Record<string, string> = {
  overview:  'Vue d\'ensemble',
  progress:  'Avancement',
  tasks:     'Tâches',
  journal:   'Journal',
  kanban:    'Kanban',
  compare:   'Comparaison',
  evolution: 'Évolution',
  gitdiff:   'Git Diff',
  tokens:    'Budget tokens',
}

/* ── Verdict système dérivé des alertes ────────────────────────── */
function deriveVerdict(alerts: Alert[]) {
  const unacked = alerts.filter(a => !a.ackedAt)
  const critical = unacked.filter(a => a.severity === 'critical').length
  const warning  = unacked.filter(a => a.severity === 'high' || a.severity === 'medium').length
  if (critical > 0) return { label: 'CRITIQUE', color: 'var(--health-crit)', count: critical }
  if (warning  > 0) return { label: 'ALERTE',   color: 'var(--health-warn)', count: warning  }
  return { label: 'STABLE', color: 'var(--health-good)', count: 0 }
}

const STATUS_ITEMS: { id: ProjectStatus; key: string; dot: string }[] = [
  { id: 'all',     key: 'status_all',     dot: '#888' },
  { id: 'active',  key: 'status_active',  dot: 'var(--health-good)' },
  { id: 'waiting', key: 'status_waiting', dot: 'var(--health-warn)' },
  { id: 'stopped', key: 'status_stopped', dot: 'var(--health-crit)' },
]

const NAV_ITEMS: { id: View; key: string; shortcut: string }[] = [
  { id: 'overview',  key: 'nav_overview', shortcut: '1' },
  { id: 'progress',  key: 'nav_progress', shortcut: '2' },
  { id: 'tasks',     key: 'nav_tasks',    shortcut: '6' },
  { id: 'journal',   key: 'nav_timeline', shortcut: '3' },
  { id: 'kanban',    key: 'nav_kanban',   shortcut: '4' },
  { id: 'compare',   key: 'nav_compare',  shortcut: '5' },
  { id: 'evolution', key: 'nav_charts',   shortcut: '' },
  { id: 'gitdiff',   key: 'nav_diff',     shortcut: '' },
  { id: 'tokens',    key: 'nav_tokens',   shortcut: '7' },
]

const PERIODS = [
  { value: 'day',     key: 'p_today' },
  { value: 'week',    key: 'p_week' },
  { value: 'month',   key: 'p_month' },
  { value: 'quarter', key: 'p_quarter' },
] as const

export default function Layout({ children, connected, lastSync, alerts = [] }: {
  children: ReactNode
  connected: boolean
  lastSync?: Date | null
  alerts?: Alert[]
}) {
  const { activeView, period, theme, statusFilter, setView, setPeriod, toggleTheme, openSearch, setStatusFilter } = useApp()
  const { lang, toggleLang } = useI18n()
  const t = useT()
  const { permission, enabled, requestPermission, setEnabled } = useDesktopNotif()
  const [collapsed, setCollapsed] = useState(false)
  const [exporting, setExporting] = useState<'pdf' | 'csv' | null>(null)

  const verdict = deriveVerdict(alerts)

  const handleExport = useCallback(async (type: 'pdf' | 'csv') => {
    if (exporting) return
    setExporting(type)
    try {
      const url = `/api/export.${type}?view=${activeView}&period=${period}`
      const res = await fetch(url)
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `kdb-${activeView}-${period}.${type}`
      a.click()
      URL.revokeObjectURL(a.href)
    } finally {
      setExporting(null)
    }
  }, [exporting, activeView, period])

  /* ── Raccourcis clavier ────────────────────────────────────── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); openSearch(); return }
      if (e.key === 'D') { toggleTheme(); return }
      if (e.key === 'L') { toggleLang(); return }
      if (e.key === 'R') { triggerForceSync(); return }
      if (e.key === '1') setView('overview')
      if (e.key === '2') setView('progress')
      if (e.key === '3') setView('journal')
      if (e.key === '4') setView('kanban')
      if (e.key === '5') setView('compare')
      if (e.key === '6') setView('tasks')
      if (e.key === '7') setView('tokens')
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggleTheme, toggleLang, openSearch, setView])

  return (
    <div className={`layout${collapsed ? ' sidebar-collapsed' : ''}`}>
      {/* ── Sidebar ──────────────────────────────────────────── */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-logo-wrap">
            <img src="/logo-kaytech.jpg" alt="KayTech" className="brand-logo" />
          </div>
          <div className="brand-text-wrap">
            <div className="brand-name">{t('appName')}</div>
            <div className="brand-sub">{t('appSub')}</div>
          </div>
          <button
            className="sidebar-collapse-btn"
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Déplier la sidebar' : 'Réduire la sidebar'}
            title={collapsed ? 'Déplier' : 'Réduire'}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {collapsed
                ? <path d="M5 3l6 5-6 5"/>
                : <path d="M11 3l-6 5 6 5"/>
              }
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ id, key, shortcut }) => (
            <button
              key={id}
              className={`nav-item${activeView === id ? ' active' : ''}`}
              onClick={() => setView(id)}
              title={collapsed ? VIEW_LABELS[id] ?? t(key) : undefined}
            >
              <span className="nav-icon">{NAV_ICONS[id]}</span>
              <span className="nav-label">{t(key)}</span>
              {shortcut && !collapsed && <span className="nav-shortcut">{shortcut}</span>}
            </button>
          ))}
        </nav>

        {/* ── Section Statut projets ───────────────────────── */}
        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-label">{t('status_section')}</div>}
          {STATUS_ITEMS.map(({ id, key, dot }) => (
            <button
              key={id}
              className={`nav-item status-item${statusFilter === id ? ' active' : ''}`}
              onClick={() => { setStatusFilter(id); setView('overview') }}
              title={collapsed ? t(key) : undefined}
            >
              <span className="status-dot" style={{ background: dot }} />
              <span className="nav-label">{t(key)}</span>
            </button>
          ))}
        </div>

        {/* ── Légende des couleurs ─────────────────────────── */}
        {!collapsed && <ColorLegend />}

        <div className="sidebar-footer">
          <div className={`conn-dot ${connected ? 'good' : 'bad'}`} />
          {!collapsed && <span className="conn-text">{connected ? t('connected') : 'Déconnecté'}</span>}
          <button
            aria-label={t('reset_prefs')}
            title={t('reset_prefs')}
            onClick={() => { resetPersistedPreferences(); window.location.reload() }}
            className="reset-prefs-btn"
          >↺</button>
        </div>
      </aside>

      {/* ── Contenu principal ────────────────────────────────── */}
      <div className="main-wrap">
        {/* ── Topbar ─────────────────────────────────────────── */}
        <header className="topbar">
          <div className="topbar-left">
            <button className="search-btn" onClick={openSearch} title="Ctrl+K">
              <span className="search-icon">⌕</span>
              <span className="search-hint">{t('search')}</span>
              <kbd>Ctrl+K</kbd>
            </button>
            <div className="topbar-sep" />
            <span className="topbar-view-title">{VIEW_LABELS[activeView] ?? activeView}</span>
          </div>

          <div className="period-tabs">
            {PERIODS.map(({ value, key }) => (
              <button
                key={value}
                className={`period-btn${period === value ? ' active' : ''}`}
                onClick={() => setPeriod(value)}
              >
                {t(key)}
              </button>
            ))}
          </div>

          <div className="topbar-right">
            {/* ── Verdict chip — toujours visible ─────────── */}
            <div className="verdict-chip" style={{ color: verdict.color }}>
              <span className="verdict-dot" style={{ background: verdict.color }} />
              <span>{verdict.label}</span>
              {verdict.count > 0 && <span className="verdict-count">{verdict.count}</span>}
            </div>
            <div className="topbar-sep" />
            <button
              className={`icon-btn${exporting === 'pdf' ? ' loading' : ''}`}
              onClick={() => handleExport('pdf')}
              disabled={!!exporting}
              title={t('export_pdf')}
              aria-label="Exporter en PDF"
            >
              {exporting === 'pdf' ? '…' : 'PDF'}
            </button>
            <button
              className={`icon-btn${exporting === 'csv' ? ' loading' : ''}`}
              onClick={() => handleExport('csv')}
              disabled={!!exporting}
              title={t('export_csv')}
              aria-label="Exporter en CSV"
            >
              {exporting === 'csv' ? '…' : 'CSV'}
            </button>
            <button className="icon-btn" onClick={toggleLang} title="L">
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            <button className="icon-btn" onClick={toggleTheme} title="D">
              {theme === 'dark' ? '☀' : '☾'}
            </button>
            {permission !== 'unsupported' && permission !== 'denied' && (
              <button
                className="icon-btn"
                title={enabled ? t('notif_disable') : t('notif_enable')}
                onClick={() => {
                  if (permission === 'default') { void requestPermission(); return }
                  setEnabled(!enabled)
                }}
                style={{ opacity: enabled && permission === 'granted' ? 1 : 0.4 }}
              >
                🔔
              </button>
            )}
          </div>
        </header>

        <main className="page-content">
          <div key={activeView} className="view-anim">
            {children}
          </div>
        </main>

        {/* ── Footer ─────────────────────────────────────────── */}
        <footer className="page-footer">
          <span className={`conn-dot ${connected ? 'good' : 'bad'}`} />
          <span>{connected ? t('syncing') : 'Déconnecté du daemon — reconnexion…'}</span>
          {lastSync && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px' }}>
              {t('next_sync')} : {lastSync.toLocaleTimeString()}
            </span>
          )}
          <span className="footer-path">{t('foot')}</span>
        </footer>
      </div>
    </div>
  )
}
